import React, { forwardRef, useMemo } from 'react';
import { translate } from '../../Services/translation';
import OrderDetailProductCard from './ui/OrderDetailProductCard';
import { Order } from '../../Types/Order';
import { EditorStringModel } from '../../Types/EditorString';
import clsx from 'clsx';
import Icon from '../Icon';

interface OrderDetailProps {
  order: Order;
  isConfirmation: boolean;
  confirmationTitle?: string;
  confirmationText?: EditorStringModel;
  confirmationExtraText?: EditorStringModel;
}

const OrderDetail = forwardRef<HTMLDivElement, OrderDetailProps>(
  (
    {
      order,
      isConfirmation,
      confirmationTitle,
      confirmationText,
      confirmationExtraText,
    },
    ref
  ) => {
    const deliveryWithTracking = useMemo(
      () => order.deliveries.find((d) => !!d.trackingUrl),
      [order.deliveries]
    );
    const trackingUrl = deliveryWithTracking?.trackingUrl;
    const trackingReference = deliveryWithTracking?.trackingReference;

    const { shippingAddress } = order.shippingInfo;

    return (
      <div
        ref={ref}
        className={clsx(
          'md:container flex flex-col gap-4 bg-gray-100 pb-6 pt-6',
          isConfirmation
            ? ' lg:items-center lg:pb-12 lg:pt-12'
            : 'mx-[-0.8rem] my-0 mb-6 md:mx-[-1.2rem] md:my-0 md:mb-6 lg:m-[unset] lg:items-center'
        )}
      >
        {isConfirmation ? (
          <div className="bg-white p-4 md:rounded lg:w-full lg:max-w-[660px] lg:p-6">
            <h1 className="mb-2">{confirmationTitle}</h1>
            <div className="flex flex-col gap-2 text-lg lg:flex-row lg:gap-6">
              <span>
                {translate('general.ordernumber')}:
                <span className="font-bold">{order.externalOrderID}</span>
              </span>
              <span>
                {translate('orderdetail.orderdate')}:
                <span className="font-bold"> {order.orderDate}</span>
              </span>
            </div>
            {order.alternateOrderNumber && (
              <span className="gap-2 text-lg">
                {translate('checkout.mail.extra-reference')}{' '}
                <span className="font-bold">
                  {order.alternateOrderNumber}
                </span>
              </span>
            )}
            {confirmationText?.value && (
              <div
                className="mt-2 text-lg"
                dangerouslySetInnerHTML={{
                  __html: confirmationText?.value,
                }}
              ></div>
            )}
          </div>
        ) : (
          <div className="bg-white p-4 md:rounded lg:w-full lg:max-w-[660px] lg:p-6">
            <h1 className="mb-2">
              {translate('general.ordernumber')}: {order.externalOrderID}
            </h1>
            <div className="flex flex-col gap-2 text-lg lg:flex-row lg:gap-6">
              <span>
                {translate('orderdetail.orderdate')}:
                <span className="font-bold"> {order.orderDate}</span>
              </span>
              <span>
                {translate('orderdetail.orderstatus')}:{' '}
                <span className="font-bold">
                  {translate(
                    `order-status.${order.orderStatus.toLowerCase()}`
                  )}
                </span>
              </span>
              {trackingReference && (
                <span className="flex">
                  {translate('orderdetail.trackingnumber')}:
                  <a
                    className="ml-1 flex items-center underline"
                    href={trackingUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span className="font-bold">{trackingReference}</span>
                    <Icon
                      name="arrow-square-out-fill"
                      className="ml-1 inline-block h-4 w-4"
                    />
                  </a>
                </span>
              )}
            </div>
            {order.alternateOrderNumber && (
              <span className="gap-2 text-lg">
                {translate('checkout.mail.extra-reference')}{' '}
                <span className="font-bold">
                  {order.alternateOrderNumber}
                </span>
              </span>
            )}
          </div>
        )}
        <div className="flex flex-col bg-white p-0 md:rounded lg:w-full lg:max-w-[660px] [&>_div:last-child_&_hr]:hidden">
          {order.orderRows.map((product) => (
            <OrderDetailProductCard
              product={product}
              key={product.deliveryId}
            />
          ))}
          <div className="flex flex-col gap-1 p-4 text-lg lg:p-6">
            <div className="flex justify-between">
              <span>{translate('checkout.order.sum')}</span>
              <span>{order.productTotal}</span>
            </div>
            {order.orderTotalDeliveryCost && (
              <div className="flex justify-between">
                <span>{translate('checkout.order.shipping')}</span>
                <span>{order.orderTotalDeliveryCost}</span>
              </div>
            )}
            {order.orderTotalFee && (
              <div className="flex justify-between">
                <span>{translate('checkout.order.fee')}</span>
                <span>{order.orderTotalFee}</span>
              </div>
            )}
            {order.orderTotalDiscountAmount && (
              <div className="flex justify-between">
                <span>{translate('checkout.order.discount')}</span>
                <span>{order.orderTotalDiscountAmount}</span>
              </div>
            )}
            <div className="text-bold flex justify-between">
              <h3>{translate('orderdetail.grandtotal')}</h3>
              <h3>{order.orderGrandTotal}</h3>
            </div>
            <div className="flex justify-between">
              <span>{translate('checkout.order.vat')}</span>
              <span>{order.orderTotalVat}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-6 bg-white p-4 md:grid md:grid-cols-2 md:rounded lg:w-full lg:max-w-[660px] lg:p-6">
          {shippingAddress && (
            <>
              <div className="flex flex-col gap-2">
                <h4>{translate('orderdetail.contactdetails')}</h4>
                <div className="flex flex-col text-lg">
                  <span>
                    {shippingAddress.firstName ??
                      order.customerInfo.firstName}{' '}
                    {shippingAddress.lastName ??
                      order.customerInfo.lastName}
                  </span>
                  <span>{shippingAddress.phoneNumber}</span>
                  <span>{shippingAddress.email}</span>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <h4>{translate('orderdetail.deliveryaddress')}</h4>
                <div className="flex flex-col text-lg">
                  {!shippingAddress.organizationName && (
                    <span>
                      {shippingAddress.firstName}{' '}
                      {shippingAddress.lastName}
                    </span>
                  )}
                  {shippingAddress.organizationName && (
                    <span>{shippingAddress.organizationName}</span>
                  )}
                  <span>{shippingAddress.address1}</span>
                  {shippingAddress.address2 && (
                    <span>{shippingAddress.address2}</span>
                  )}
                  <span>
                    {shippingAddress.zipCode} {shippingAddress.city}
                  </span>
                  <span>{shippingAddress.country}</span>
                </div>
              </div>
            </>
          )}
          <div className="flex flex-col gap-2">
            <h4>{translate('orderdetail.deliverymethod')}</h4>
            <span className="text-lg">{order.deliveryMethod}</span>
          </div>
        </div>
        {isConfirmation && confirmationExtraText?.value && (
          <div
            className="bg-white p-4 text-lg md:rounded lg:w-full lg:max-w-[660px] lg:p-6"
            dangerouslySetInnerHTML={{
              __html: confirmationExtraText?.value,
            }}
          ></div>
        )}
      </div>
    );
  }
);

OrderDetail.displayName = 'orderDetail';

export default OrderDetail;
